import { ActionContext, ActionTree } from "vuex";

import { RestClient } from "@elite/restclient";

import {
    ICheckoutModel,
    IOrderModel,
    IPromotionModel,
    IPromotionRequestModel,
    ISettingsModel,
    IValidationRules,
    TrackingRequest,
    IAddressModel
} from "../../models";
import { getCheckout } from "../../models/factories";
import { AppGetterTypes } from "../getters";
import IAppState from "../IAppState";
import { AppMutationTypes } from "../mutations";
import AppActionTypes from "./AppActionTypes";

export type SetPromotion = (params: IPromotionRequestModel) => Promise<void>;
export type SetCheckout = (order: ICheckoutModel) => void;
export type SetSettings = () => Promise<void>;
export type SetValidationRules = () => Promise<void>;
export type AddTracking = (request: TrackingRequest) => Promise<void>;
export type PutAddress = (address: IAddressModel) => Promise<IAddressModel | null>;

/**
 * Retrieves an already-placed order model by the order uid.
 * Commits the order model to the state.
 * See OrderController in the web project for information on how this works.
 */
export type GetOrder = (id: string) => Promise<void>;

export const restClient = new RestClient(process.env.VUE_APP_API_BASE_URL);

const actions: ActionTree<IAppState, IAppState> = {
    async [AppActionTypes.getSettings](context): Promise<void> {
        const response = await restClient.getJson<ISettingsModel>(
            "/api/Setting"
        );

        context.commit(AppMutationTypes.setSettings, response.data);
    },

    async [AppActionTypes.getValidationRules](context): Promise<void> {
        const response = await restClient.getJson<IValidationRules>(
            "/api/ValidationRule"
        );

        context.commit(AppMutationTypes.setValidationRules, response.data);
    },

    async [AppActionTypes.getPromotion](
        context: ActionContext<IAppState, IAppState>,
        params: IPromotionRequestModel
    ) {
        const { slug, promotionCode, url } = params;

        const encodedUrl = encodeURIComponent(url);
        let requestUrl = `/api/Promotion/${slug}?url=${encodedUrl}`;
        if (promotionCode) {
            requestUrl += `&promotionCode=${promotionCode}`;
        }

        const response = await restClient.getJson<IPromotionModel>(requestUrl);

        if (response.status === 200 && response.data) {
            // Set page title and body color for the document
            context.commit(
                AppMutationTypes.setDocumentTitleAndColor,
                response.data
            );
            context.commit(AppMutationTypes.setPromotion, response.data);

            if (!context.getters[AppGetterTypes.order]) {
                const checkout = getCheckout(
                    response.data.id,
                    promotionCode || response.data.promotionCode || undefined
                );
                context.commit(AppMutationTypes.setCheckout, checkout);
            }
        }
    },

    /**
     * Retrieves an already-placed order model by id.
     * Commits the order model to the state.
     * See OrderController in the web project for information on how this works.
     */
    async [AppActionTypes.getOrder](
        context: ActionContext<IAppState, IAppState>,
        orderId: string
    ) {
        const response = await restClient.getJson<IOrderModel>(
            `/api/Order/${orderId}`
        );

        if (response.status === 200 && response.data) {
            context.commit(AppMutationTypes.setOrder, response.data);
            context.commit(
                AppMutationTypes.setPromotion,
                response.data.promotion
            );
        }
    },

    async [AppActionTypes.addTracking](
        context: ActionContext<IAppState, IAppState>,
        request: TrackingRequest
    ) {
        await restClient.postJson(
            `/api/Progress/${request.type}`,
            request.tracking
        );
    },

    /** This action PUTs an Address to the AddressController, which validates it, ensures accuracy, and returns the validated Address. */
    async [AppActionTypes.putAddress](context: ActionContext<IAppState, IAppState>, address: IAddressModel) {
        const response = await restClient.putJson<IAddressModel>(
            `/api/Address`,
            address
        );

        return response.data;
    }
};

export default actions;

export { AppActionTypes };
